import type { FieldValues, Path, UseFormReturn } from 'react-hook-form'

import { GENERIC_ERROR } from '@/lib/errors'
import { fillApplicationError } from '@/models/fillApplication'

export const SERVER_ERROR = 'root.server'

function messageOf(value: unknown): string | null {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return value.map(messageOf).find((message) => !!message) ?? null
  return null
}

/** Puts a refused save's messages under the fields they name (`fields` maps API keys to form fields); the rest goes on the form. */
export function applyServerErrors<V extends FieldValues>(
  form: UseFormReturn<V>,
  error: unknown,
  fields: Record<string, Path<V>>
): void {
  const refusal = fillApplicationError(error)
  if (refusal) {
    form.setError(SERVER_ERROR, { type: refusal.code, message: refusal.detail })
    return
  }
  const data = (error as { response?: { data?: unknown } } | null | undefined)?.response?.data
  const body =
    data && typeof data === 'object' && !Array.isArray(data) ? (data as Record<string, unknown>) : {}
  let placed = false
  for (const [key, value] of Object.entries(body)) {
    const field = fields[key]
    const message = messageOf(value)
    if (field && message) {
      form.setError(field, { type: 'server', message })
      placed = true
    }
  }
  const general = messageOf(body.error) ?? messageOf(body.detail) ?? messageOf(body.non_field_errors)
  // The owner endpoint names the field its message is about, e.g. {"error": "...", "field": "email"}.
  const namedField = typeof body.field === 'string' ? fields[body.field] : undefined
  if (general && namedField) {
    form.setError(namedField, { type: 'server', message: general })
    return
  }
  if (general || !placed) form.setError(SERVER_ERROR, { type: 'server', message: general ?? GENERIC_ERROR })
}
